import type { FC, InputHTMLAttributes } from 'react';

import cn from 'classnames';

import { FieldStatus, fieldStatusId } from './FieldStatus';

interface LabeledTextInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  id: string;
  label: string;
  error?: string;
  /** Informational text shown below the input; hidden while an error is displayed. */
  hint?: string;
  hideLabel?: boolean;
}

export const LabeledTextInput: FC<LabeledTextInputProps> = ({
  id,
  label,
  error,
  hint,
  hideLabel = false,
  className,
  type = 'text',
  ...props
}) => {
  const describedBy = fieldStatusId({ id, error, hint });
  return (
    <div className="flex flex-col gap-1.5">
      <label
        htmlFor={id}
        className={cn('text-[13px] font-medium leading-4 tracking-[-0.13px] text-black/70', { 'sr-only': hideLabel })}
      >
        {label}
        {props.required ? <span aria-hidden="true"> *</span> : null}
      </label>
      <input
        id={id}
        type={type}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={cn(
          'h-11 w-full rounded-lg border bg-white px-4 text-[14px] leading-5 tracking-[-0.14px] text-black placeholder:text-black/30 focus:outline-none focus:ring-2 disabled:opacity-60',
          error
            ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
            : 'border-black/10 focus:border-community-primary focus:ring-community-primary/20',
          className,
        )}
        {...props}
      />
      <FieldStatus id={id} error={error} hint={hint} />
    </div>
  );
};
